import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ChevronDown,
  ListFilter,
  Loader2,
  MessageSquareText,
  Search,
} from 'lucide-react';

import { Pagination } from '@/components/pagination';
import { ScopeTabs } from '@/components/scope-tabs';
import { PageTitle } from '@/components/page-title';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAgentConversationsPage } from '@/hooks/queries/agents/use-agent-conversations-page';
import { usePrimaryAgent } from '@/hooks/queries/agents/use-primary-agent';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { CONVERSATION_SORT_KEYS } from '@/services/agents/config';
import type {
  ConversationListScope,
  ConversationSortKey,
} from '@/services/agents/config';
import type { Agent } from '@/types/agent-types';

import { ChatCard } from './components/chat-card';
import { NewChatPromptBar } from './components/new-chat-prompt-bar';

const PAGE_SIZE = 21;

const DEFAULT_SORT: ConversationSortKey = 'last_message_desc';

const SORT_LABELS: Record<ConversationSortKey, string> = {
  last_message_desc: 'Recent',
  last_message_asc: 'Oldest first',
  title_asc: 'Name A→Z',
  title_desc: 'Name Z→A',
};

function ChatsSkeleton() {
  return (
    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className='h-36 w-full rounded-xl' />
      ))}
    </div>
  );
}

export function ChatsPage() {
  const { data: primaryAgent, isLoading, error, refetch } = usePrimaryAgent();

  if (isLoading) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error || !primaryAgent) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 p-6 text-center'>
        <p className='text-sm font-medium'>Chats are unavailable</p>
        <p className='max-w-md text-sm text-muted-foreground'>
          {error?.message ??
            'The organization primary agent could not be resolved.'}
        </p>
        <Button variant='outline' onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return <ChatsList agent={primaryAgent} />;
}

function ChatsList({ agent }: { agent: Agent }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isChatSharingEnabled } = useLaunchDarkly();
  const [selectedScope, setSelectedScope] =
    useState<ConversationListScope>('mine');
  // Derived rather than stored: the flag can flip mid-session, and a stored
  // 'shared' would keep listing shared chats after the tabs that switch
  // back have disappeared.
  const scope: ConversationListScope = isChatSharingEnabled
    ? selectedScope
    : 'mine';
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [searchVisible, setSearchVisible] = useState(false);

  const searchContainerRef = useRef<HTMLDivElement>(null);

  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
  const currentPage = pageFromUrl > 0 ? pageFromUrl : 1;
  const sortFromUrl = searchParams.get('sort');
  const sort =
    CONVERSATION_SORT_KEYS.find((key) => key === sortFromUrl) ?? DEFAULT_SORT;

  const { data, isLoading, error } = useAgentConversationsPage(agent.id, {
    scope,
    sort,
    search: debouncedSearch.trim(),
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  });
  const pageConversations = data?.conversations ?? [];
  const total = data?.total ?? 0;

  const handlePageChange = useCallback(
    (page: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(page));
      setSearchParams(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [searchParams, setSearchParams],
  );

  const resetPage = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get('page') !== '1') {
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      // A query typed while paged in would otherwise request a page past
      // the end of the narrowed result set.
      resetPage();
    },
    [resetPage],
  );

  const handleSearchClose = useCallback(() => {
    setSearchVisible(false);
    setSearch('');
    resetPage();
  }, [resetPage]);

  useEffect(() => {
    if (searchVisible) {
      searchContainerRef.current?.querySelector('input')?.focus();
    }
  }, [searchVisible]);

  const handleSortChange = useCallback(
    (key: ConversationSortKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('sort', key);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleScopeChange = (next: ConversationListScope) => {
    setSelectedScope(next);
    resetPage();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total);

  useEffect(() => {
    if (!isLoading && total > 0 && currentPage > totalPages) {
      handlePageChange(totalPages);
    }
  }, [currentPage, totalPages, total, isLoading, handlePageChange]);

  return (
    <div className='mx-auto flex w-full flex-col gap-6 p-8 2xl:max-w-[1280px]'>
      <div className='flex items-center gap-3'>
        <MessageSquareText
          className='size-8 shrink-0 text-foreground'
          strokeWidth={1.5}
        />
        <PageTitle title='Chat History' />
      </div>

      <NewChatPromptBar agentId={agent.id} />

      <div className='flex items-center justify-between gap-4'>
        <div className='flex items-center gap-1'>
          {isChatSharingEnabled && (
            <ScopeTabs
              value={scope}
              onValueChange={handleScopeChange}
              mineLabel='My chats'
            />
          )}
          <div
            ref={searchContainerRef}
            className={cn(
              'h-9 overflow-hidden transition-[width] duration-200 ease-out',
              searchVisible
                ? 'w-56 animate-in fade-in-0 slide-in-from-left-2'
                : 'w-9',
            )}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && searchVisible) {
                handleSearchClose();
              }
            }}
          >
            {searchVisible ? (
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder='Search chats...'
                aria-label='Search chats'
              />
            ) : (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-9 hover:text-foreground'
                onClick={() => setSearchVisible(true)}
                aria-label='Open search'
                aria-expanded={false}
              >
                <Search className='size-4' />
              </Button>
            )}
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='gap-1 text-sm font-medium'>
                <ListFilter className='size-4 shrink-0' />
                {SORT_LABELS[sort]}
                <ChevronDown className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {CONVERSATION_SORT_KEYS.map((key) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleSortChange(key)}
                >
                  {SORT_LABELS[key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isLoading ? (
        <ChatsSkeleton />
      ) : error ? (
        <div className='py-12 text-center text-muted-foreground'>
          Failed to load chats. Please try again.
        </div>
      ) : total === 0 ? (
        <div className='py-12 text-center text-muted-foreground'>
          {debouncedSearch
            ? 'No chats match your search.'
            : scope === 'shared'
              ? 'No chats have been shared with you yet.'
              : 'No chats yet. Start one above.'}
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {pageConversations.map((conversation) => (
            <ChatCard
              key={conversation.conversation_id}
              conversation={conversation}
              agentId={agent.id}
              agentName={agent.name}
            />
          ))}
        </div>
      )}

      {!isLoading && total > 0 && (
        <div className='flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-between'>
          <p className='text-sm text-muted-foreground'>
            Showing {rangeStart}–{rangeEnd} of {total} chats
          </p>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}
    </div>
  );
}
